import {
  computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { Gender, PAGE_SIZE } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetApiLang, GetLangKey, GetUserProfile, Language } from '../utilities/general';
import { FAxios } from '../utilities/file-network';

export default class PrescriptionStore {

  loading: boolean = false;

  creating: boolean = false;

  updating: boolean = false;

  updatingPrice: boolean = false;

  removing: boolean = false;

  loadingTreatment: boolean = false;

  paying: boolean = false;

  savingInsuranceRebate: boolean = false;
  markingInsuranceRebateAsPaid: boolean = false;

  generatingReceipt: boolean = false;

  generatingInstruction: boolean = false;

  generatingMedicalRecord: boolean = false;

  copying: boolean = false;

  loadingSection: boolean = false;

  tempTreatment: any = null;

  isUpdatingComplaint: boolean = false;

  isUpdatingTreatment: boolean = false;

  sendingEmail: boolean = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      loading: observable,
      creating: observable,
      updating: observable,
      updatingPrice: observable,
      removing: observable,
      loadingTreatment: observable,
      loadingSection: observable,
      paying: observable,
      savingInsuranceRebate: observable,
      markingInsuranceRebateAsPaid: observable,
      refundingPayment: observable,
      generatingReceipt: observable,
      generatingInstruction: observable,
      generatingMedicalRecord: observable,
      copying: observable,
      tempTreatment: observable,
      isUpdatingComplaint: observable,
      isUpdatingTreatment: observable,
      sendingEmail: observable,
    });
    this.rootStore = rootStore;
  }

  searchHerbs = async (skip: number, take: number, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Herb/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchHerbFormulas = async (skip: number, take: number, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Herb/Formula/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchAcupuncturePoints = async (skip: number, take: number, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Acupuncture/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchAcupuncturePointGroups = async (skip: number, take: number, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Acupuncture/AcuPointGroup/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, { ...reqModel, clinicId: GetUserProfile().currentClinicId });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createAcupuncturePointGroups = async (reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Acupuncture/AcuPointGroup?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };
  updateAcupuncturePointGroups = async (reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Acupuncture/AcuPointGroup?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };
  deleteAcupuncturePointGroups = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.delete(`/api/Acupuncture/AcuPointGroup/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // Update Acu Group Frequency
  updateAcupuncturePointGroupsFrequency = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Acupuncture/AcuPointGroup/UseFrequency/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchTreatmentHistory = async (skip: number, take: number, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Patient/TreatmentHistory/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchPreviousTreatmentHistory = async (skip: number, take: number, reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Patient/TreatmentHistory/Search/Previous/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createTreatmentHistory = async (reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.creating = true;
    try {
      const res = await Axios.post(`/api/Patient/TreatmentHistory?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creating = false;
    }
  };

  updateTreatmentHistory = async (reqModel: any, enableLoading: boolean = true, lang: Language = GetApiLang()) => {
    if (enableLoading) this.updating = true;
    this.isUpdatingTreatment = true;
    try {
      const res = await Axios.put(`/api/Patient/TreatmentHistory?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (enableLoading) this.updating = false;
      this.isUpdatingTreatment = false;
    }
  };

  getTreatmentHistory = async (id: number, lang: Language = GetApiLang()) => {
    this.loadingTreatment = true;
    try {
      const res = await Axios.get(`/api/Patient/TreatmentHistory/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingTreatment = false;
    }
  };

  getPrevTreatmentHistory = async (id: number, lang: Language = GetApiLang()) => {
    try {
      const res = await Axios.get(`/api/Patient/TreatmentHistory/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
    }
  };

  removeTreatmentHistory = async (id: number) => {
    this.removing = true;
    try {
      const res = await Axios.delete(`/api/Patient/TreatmentHistory/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.removing = false;
    }
  };

  updateTreatmentPrice = async (reqModel: any, disableLoading: boolean = false) => {
    if (!disableLoading) this.updating = true;
    else this.updatingPrice = true;
    try {
      const res = await Axios.put(`/api/Patient/TreatmentHistory/Price?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (!disableLoading) this.updating = false;
      else this.updatingPrice = false;
    }
  };

  searchOtherFees = async (patientHistoryId: number) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Patient/History/OtherFee/Search/0/0?platformId=${CURRENT_PLATFORM}`, { patientHistoryId });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  updateOtherFees = async (patientHistoryId: number, reqModel: any, disableLoading: boolean = false) => {
    if (!disableLoading) this.updating = true;
    else this.updatingPrice = true;
    try {
      const res = await Axios.put(`/api/Patient/History/OtherFees?platformId=${CURRENT_PLATFORM}`, { patientHistoryId, ...reqModel });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (!disableLoading) this.updating = false;
      else this.updatingPrice = false;
    }
  };

  makePayment = async (reqModel: any) => {
    this.paying = true;
    try {
      const clinicId = GetUserProfile()?.currentClinicId || 0;
      const res = await Axios.put(`/api/Patient/History/Payment?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.paying = false;
    }
  };

  saveInsuranceRebate = async (reqModel: { patientInsuranceId: number, rebateAmount: number, paymentMethod: string, patientHistoryId: number; }) => {
    this.savingInsuranceRebate = true;
    try {
      const clinicId = GetUserProfile()?.currentClinicId || 0;
      const res = await Axios.post(`/api/Patient/History/Insurance?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingInsuranceRebate = false;
    }
  };

  markInsuranceRebateAsPaid = async (reqModel: { id: number, patientInsuranceId: number, rebateAmount: number, paymentMethod: string, paymentStatus: string, merchantId?: string, providerId?: string, rrnNumber?: string; }) => {
    this.markingInsuranceRebateAsPaid = true;
    try {
      const clinicId = GetUserProfile()?.currentClinicId || 0;
      const res = await Axios.put(`/api/Patient/History/Insurance?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.markingInsuranceRebateAsPaid = false;
    }
  };

  // Rolls the record back to unpaid once its Hicaps terminal transaction(s)
  // (rebate claim, balance sale, or both) have actually been cancelled/
  // refunded on the terminal — see HicapsRefundDialog, which calls this only
  // after those terminal calls succeed.
  refundingPayment: boolean = false;
  // isGapRefundOnly: when the record's rebate and balance were both paid via
  // Hicaps but the practitioner chose to refund only the balance ("gap")
  // payment and leave the health fund claim as-is, the backend needs telling
  // — omitted entirely (not sent as `false`) for every other refund.
  refundPayment = async (historyId: number, isGapRefundOnly?: boolean) => {
    this.refundingPayment = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Payment/Refund/${historyId}?platformId=${CURRENT_PLATFORM}${isGapRefundOnly ? '&isGapRefundOnly=true' : ''}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.refundingPayment = false;
    }
  };

  updateTempTreatment = (reqModel: any) => {
    this.tempTreatment = reqModel;
  };


  generateReceipt = async (patientHistoryId: number) => {
    this.generatingReceipt = true;
    try {
      const res = await FAxios.get(`/api/Patient/History/Payment/Receipt/${patientHistoryId}?platformId=${CURRENT_PLATFORM}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingReceipt = false;
    }
  };


  generateReceiptPdf = async (patientHistoryId: number) => {
    this.generatingReceipt = true;
    try {
      const res = await FAxios.get(`/api/Patient/History/Payment/Receipt/Pdf/${patientHistoryId}?platformId=${CURRENT_PLATFORM}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingReceipt = false;
    }
  };


  generateReceiptWord = async (patientHistoryId: number) => {
    this.generatingReceipt = true;
    try {
      const res = await FAxios.get(`/api/Patient/History/Payment/Receipt/Word/${patientHistoryId}?platformId=${CURRENT_PLATFORM}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingReceipt = false;
    }
  };


  sendReceiptViaEmail = async (id: number, email: string) => {
    this.sendingEmail = true;
    try {
      const res = await FAxios.post(`/api/Patient/History/Payment/Receipt/Email?platformId=${CURRENT_PLATFORM}`, { id, email });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.sendingEmail = false;
    }
  };

  generateInstruction = async (patientHistoryId: number, isTCM: boolean = true) => {
    this.generatingInstruction = true;
    try {
      const res = await FAxios.get(`/api/Patient/History/Instruction/${isTCM ? "TCM" : "MAS"}/${patientHistoryId}?platformId=${CURRENT_PLATFORM}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingInstruction = false;
    }
  };

  saveExamination = async (medicalHistoryId: number, reqModel: any, enableLoading: boolean = true) => {
    if (enableLoading) this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/Examination?patientHistoryId=${medicalHistoryId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (enableLoading) this.updating = false;
    }
  };

  getExamination = async (medicalHistoryId: number) => {
    this.loadingSection = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/Examination?patientHistoryId=${medicalHistoryId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSection = false;
    }
  };

  searchDeterminationTcm = async (searchTerm: string) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Option/DeterminationTcm/Search/0/0?platformId=${CURRENT_PLATFORM}`, { searchTerm });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getAuxiliaryTest = async (historyId: number) => {
    this.loadingSection = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/AuxiliaryTest?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSection = false;
    }
  };

  saveAuxiliaryTest = async (historyId: number, reqModel: any, enableLoading: boolean = true) => {
    if (enableLoading) this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/AuxiliaryTest?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (enableLoading) this.updating = false;
    }
  };

  getPersonalHealth = async (historyId: number) => {
    this.loadingSection = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/PersonalHealth?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSection = false;
    }
  };

  updatePersonalHealth = async (historyId: number, reqModel: any, enableLoading: boolean = true) => {
    if (enableLoading) this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/PersonalHealth?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (enableLoading) this.updating = false;
    }
  };

  getPreviousHistory = async (historyId: number) => {
    this.loadingSection = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/PreviousHistory?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSection = false;
    }
  };

  updatePreviousHistory = async (historyId: number, reqModel: any, enableLoading: boolean = true) => {
    if (enableLoading) this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/PreviousHistory?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (enableLoading) this.updating = false;
    }
  };

  getComplaint = async (historyId: number) => {
    this.loadingSection = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/Complaint?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSection = false;
    }
  };

  updateComplaint = async (historyId: number, reqModel: any, enableLoading: boolean = true) => {
    if (this.isUpdatingComplaint) return;
    this.isUpdatingComplaint = true;
    if (enableLoading) this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/Complaint?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (enableLoading) this.updating = false;
      this.isUpdatingComplaint = false;
    }
  };

  getDiagnosis = async (historyId: number) => {
    this.loadingSection;
    try {
      const clinicId = GetUserProfile()?.currentClinicId || 0;
      const res = await Axios.get(`/api/Patient/History/Diagnosis/${historyId}?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSection = false;
    }
  };


  getDiagnosisEvaluation = async (historyId: number) => {
    this.loadingSection = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/Evaluation?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSection = false;
    }
  };

  updateDiagnosisEvaluation = async (historyId: number, reqModel: any, enableLoading: boolean = true) => {
    if (enableLoading) this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/Evaluation?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (enableLoading) this.updating = false;
    }
  };

  getPainInfo = async (historyId: number) => {
    this.loadingSection = true;
    try {
      const res = await Axios.get(`/api/Patient/History/Diagnosis/PainInfo?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingSection = false;
    }
  };

  updatePainInfo = async (historyId: number, reqModel: any, enableLoading: boolean = true) => {
    if (enableLoading) this.updating = true;
    try {
      const res = await Axios.put(`/api/Patient/History/Diagnosis/PainInfo?patientHistoryId=${historyId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      if (enableLoading) this.updating = false;
    }
  };

  downloadMedicalRecordFile = async (id: string) => {
    this.generatingMedicalRecord = true;
    try {
      const res = await FAxios.post(`/api/Patient/History/Record/Word/${id}?platformId=${CURRENT_PLATFORM}`, {}, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingMedicalRecord = false;
    }
  };

  downloadMedicalRecordPdf = async (id: string) => {
    this.generatingMedicalRecord = true;
    try {
      const res = await FAxios.post(`/api/Patient/History/Record/Pdf/${id}?platformId=${CURRENT_PLATFORM}`, {}, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingMedicalRecord = false;
    }
  };

  zipMedicalRecordFiles = async (reqModel: any) => {
    this.generatingMedicalRecord = true;
    try {
      const res = await FAxios.post(`/api/Patient/History/Record/Word/Zip?platformId=${CURRENT_PLATFORM}`, reqModel, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingMedicalRecord = false;
    }
  };

  downloadSicknessPdf = async (reqModel: any) => {
    this.generatingMedicalRecord = true;
    try {
      const res = await FAxios.post(`/api/Patient/History/Sickness/Pdf?platformId=${CURRENT_PLATFORM}`, reqModel, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingMedicalRecord = false;
    }
  };

  downloadSicknessWord = async (reqModel: any) => {
    this.generatingMedicalRecord = true;
    try {
      const res = await FAxios.post(`/api/Patient/History/Sickness/Word?platformId=${CURRENT_PLATFORM}`, reqModel, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingMedicalRecord = false;
    }
  };



}
